<template>
  <div id="license-form" class="h-full">
    <detail-content-layout
      :label="typeLabel"
      :on-button-click="handleAddUpdate"
    >
      <div>
        <component
          :is="currentFormComponent"
          :key="category"
          ref="licenseCategoryForm"
          v-bind="formComponentProps"
          @license-type-change="handleLicenseTypeChange"
        />
        <div class="flex gap-x-4 justify-center">
          <Button
            type="filled"
            colorType="cancel"
            label="キャンセル"
            @click="cancelEdit"
          ></Button>
          <Button
            type="filled"
            colorType="normal"
            @click="save"
            label="保存する"
          ></Button>
        </div>
      </div>
    </detail-content-layout>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import DetailContentLayout from "../../components/DetailContentLayout.vue";
import Button from "../../components/Button.vue";
import {
  isSeamansBookCategory,
  isCOCCategory,
} from "../../constants/License.js";
import SeamansBookForm from "../components/form/SeamansBookForm.vue";
import CocForm from "../components/form/CocForm.vue";
import OtherForm from "../components/form/OtherForm.vue";
import { LicenseCategoryLabels } from "../../constants/License.js";
import { ColorMixin } from "@/mixins";

export default {
  name: "LicenseForm",
  mixins: [ColorMixin],
  components: {
    DetailContentLayout,
    Button,
    SeamansBookForm,
    CocForm,
    OtherForm,
  },
  props: {
    crewId: {
      type: String,
      required: true,
    },
    category: {
      type: String,
      required: true,
    },
    license: {
      type: String,
      default: null,
    },
  },
  computed: {
    ...mapGetters("crewSettingsLicense", ["getLicenseOptionsByCategory"]),

    /** 現在の資格タイプのラベルを返す */
    typeLabel() {
      return this.selectedLicenseLabel;
    },

    /** カテゴリがCOCかを判定 */
    isCOC() {
      return isCOCCategory(this.category);
    },

    /** カテゴリが船員手帳かを判定 */
    isSeamanBook() {
      return isSeamansBookCategory(this.category);
    },

    /** ストアから資格オプションを取得 */
    licenseOptions() {
      return this.getLicenseOptionsByCategory(this.category);
    },

    // ========== 動的フォーム処理 ==========
    /** カテゴリに応じた動的フォームコンポーネントを返す */
    currentFormComponent() {
      if (this.isSeamanBook) {
        return SeamansBookForm;
      }
      if (this.isCOC) {
        return CocForm;
      }
      return OtherForm;
    },

    /** 各フォームコンポーネントへ渡すpropsオブジェクト */
    formComponentProps() {
      return {
        license: this.license,
        licenseOptions: this.licenseOptions,
      };
    },
  },
  data() {
    return {
      selectedLicenseLabel: "",
    };
  },
  async mounted() {
    await this.fetchLicenseCategories();
  },

  methods: {
    /** 新規追加/更新ボタンからの呼び出し */
    handleAddUpdate() {
      this.save();
    },

    /** 子フォームから資格タイプ変更時に呼ばれるハンドラ */
    handleLicenseTypeChange(label) {
      this.selectedLicenseLabel = label;
    },

    /** キャンセルボタンからダッシュボードに遷移 */
    cancelEdit() {
      this.$router.push({
        path: `/enterprise/management/crews/crew-settings/license/dashboard/${this.crewId}`,
      });
    },

    /** フォーム検証とペイロード構築、API保存処理 */
    async save() {
      const formRef = this.$refs.licenseCategoryForm;
      if (!formRef || typeof formRef.validate !== "function") {
        return;
      }

      // 子フォームのバリデーションを実行
      const isValid = formRef.validate();
      if (!isValid) {
        this.$vs.notify({
          title: "バリデーションエラー",
          text: "入力内容に誤りがあります。各項目を確認してください。",
          color: "danger",
          iconPack: "feather",
          icon: "icon-alert-circle",
        });
        return;
      }

      // 子フォームからペイロードを取得
      const payloadData = formRef.getPayload ? formRef.getPayload() : {};

      // payloadからfilesを抽出
      const files = payloadData.files || [];

      // APIペイロード用に files を除外したデータを構築
      const payload = { ...payloadData };
      delete payload.files;

      // FormDataを生成
      const formData = new FormData();
      formData.append(
        "json",
        new Blob([JSON.stringify(payload)], {
          type: "application/json",
        })
      );
      files.forEach(file => {
        formData.append("files", file);
      });

      // カテゴリラベルを取得
      const categoryLabel = LicenseCategoryLabels[this.category] || "資格";
      // ライセンス名を取得
      const licenseName = this.selectedLicenseLabel || "";

      try {
        this.$vs.loading({ color: this.AISEA_COLOR.BLUE });
        await this.$store.dispatch("crewSettingsLicense/addHavingLicense", {
          enterpriseCrewId: this.crewId,
          payload: formData,
        });

        this.$vs.notify({
          title: `${categoryLabel}登録の成功`,
          text: `${licenseName}登録に成功しました`,
          color: "success",
          iconPack: "feather",
          icon: "icon-check",
        });

        // 登録成功したらカテゴリ別の資格一覧ページに遷移
        this.$router.push({
          path: `/enterprise/management/crews/crew-settings/license/${this.crewId}`,
          query: { category: this.category },
        });
      } catch (error) {
        console.error(error);
        this.$vs.notify({
          title: `${categoryLabel}登録の失敗`,
          text: `${licenseName}登録に失敗しました。時間を空けて再度試みてください。`,
          color: "danger",
          iconPack: "feather",
          icon: "icon-alert-circle",
        });
      } finally {
        this.$vs.loading.close();
      }
    },

    /**
     * カテゴリに応じた資格（免許）オプションリストを取得
     * Vuexストアを使用してAPIから動的に取得
     */
    async fetchLicenseCategories() {
      try {
        this.$vs.loading({ color: this.AISEA_COLOR.BLUE });

        // ストアから資格定義を取得（カテゴリでフィルタ）
        await this.$store.dispatch(
          "crewSettingsLicense/fetchLicenseDefinition",
          {
            category: this.category,
          }
        );
      } catch (error) {
        console.error("資格一覧の取得に失敗しました:", error);
        this.$vs.notify({
          title: "資格一覧取得",
          text: "資格一覧を取得できませんでした",
          color: "danger",
          iconPack: "feather",
          icon: "icon-alert-circle",
        });
      } finally {
        this.$vs.loading.close();
      }
    },
  },
};
</script>
